import { type ComponentType, useMemo } from 'react';
import { HStack, IconDialogCheck, ListItem, type TestProps } from '@direct-frontend/components';
import { useLocation } from '@direct-frontend/modules-router';
import { useGetOnboardingStages } from '#shared/api/onboarding';
import { YCP_CABINET_NAVIGATION } from '#shared/lib/const';
import { useNavigateWithSearchParams } from '#shared/lib/router';
import { trackMerchantsGoal } from '#shared/lib/trackGoal';
import { isActiveStep } from '../../lib/isActiveStep';
import { type OnboardingStepProps, type OnboardingSubtaskProps } from '../../lib/types';
import { Step } from '../Step';

export type VariantPublishButtonProps = {
    name: string;
    isDisabled: boolean;
} & TestProps;

type OnboardingStepsRendererProps = {
    publishSlug: string;
    stepsProps: Record<string, OnboardingStepProps>;
    subtasksProps: Record<string, OnboardingSubtaskProps>;
    PublishButton: ComponentType<VariantPublishButtonProps>;
} & TestProps;

export function OnboardingStepsRenderer({
    publishSlug,
    stepsProps,
    subtasksProps,
    PublishButton,
    'data-testid': dataTestId = 'OnboardingStepper',
}: OnboardingStepsRendererProps) {
    const navigate = useNavigateWithSearchParams();
    const { data, refetch } = useGetOnboardingStages();
    const location = useLocation();

    const steps = useMemo(() => {
        const stages = data?.stages ?? [];

        return stages
            .map((stage) => {
                const stepProps = stepsProps[stage.slug];

                if (!stepProps) {
                    return null;
                }

                return {
                    ...stage,
                    ...stepProps,
                    name: stepProps.name ?? stage.name,
                    subtasks: stage.subtasks
                        .map((subtask) => {
                            const subtaskProps = subtasksProps[subtask.slug];

                            if (!subtaskProps) {
                                return null;
                            }

                            const isDisabled = Boolean(
                                subtaskProps.blockedBy &&
                                    stage.subtasks.some(
                                        ({ slug, is_completed }) =>
                                            slug === subtaskProps.blockedBy && is_completed === false
                                    )
                            );

                            return { ...subtask, ...subtaskProps, isDisabled };
                        })
                        .filter((subtask) => subtask !== null)
                        .sort((a, b) => a.orderWithinStage - b.orderWithinStage),
                };
            })
            .filter((stage) => stage !== null);
    }, [data?.stages, stepsProps, subtasksProps]);

    const isPublishDisabled = useMemo(
        () => (data?.stages ?? []).some((step) => step.slug !== publishSlug && !step.is_completed),
        [publishSlug, data?.stages]
    );

    return (
        <HStack data-testid={dataTestId} gap="8">
            {steps.map((step) =>
                step.slug === publishSlug ? (
                    <PublishButton
                        data-testid={`${dataTestId}.PublishButton`}
                        isDisabled={isPublishDisabled}
                        key={step.slug}
                        name={step.name}
                    />
                ) : (
                    <Step
                        refetch={refetch}
                        key={step.slug}
                        status={step.is_completed ? 'done' : 'pending'}
                        name={step.name}
                        href={step.href}
                        description={step.description}
                        isActive={isActiveStep(location, step)}
                    >
                        {step.subtasks.map((subtask) => (
                            <ListItem
                                onClick={() => {
                                    refetch();
                                    navigate({ pathname: subtask.href, search: subtask.search, hash: subtask.hash });
                                    trackMerchantsGoal(YCP_CABINET_NAVIGATION, {
                                        menuType: 'HEADER_ONBOARDING_STEPS',
                                        buttonType: subtask.href,
                                    });
                                }}
                                isDisabled={subtask.isDisabled}
                                iconRight={
                                    subtask.is_completed
                                        ? (props) => <IconDialogCheck {...props} size="24" color="green" />
                                        : undefined
                                }
                                key={subtask.slug}
                            >
                                {subtask.name}
                            </ListItem>
                        ))}
                    </Step>
                )
            )}
        </HStack>
    );
}
